import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { useCockpitState } from "./cockpitState";
import { isWeekFocusFresh } from "../../../lib/cockpitState";
import { RitualSession } from "./Rituals";
import { Button } from "../../ui/button";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Spec §3.b.2 — the manager's ritual, once a week. Opens the existing weekly
 *  RitualSession; its closing intention lands in config.cockpit.weekFocus,
 *  which the Season strip and this banner both read. */
export const WeeklyReviewBanner = () => {
  const { t, i18n } = useTranslation();
  const { compassId, weekFocus, lastWeeklyReviewAt } = useCockpitState();
  const [open, setOpen] = useState(false);
  if (!compassId) return null;

  const reviewedAt = lastWeeklyReviewAt ? new Date(lastWeeklyReviewAt).getTime() : null;
  const fresh = reviewedAt != null && Date.now() - reviewedAt < WEEK_MS;
  // A weekly review can close without setting a new intention, leaving weekFocus
  // untouched — so "a review happened recently" (fresh) is not the same fact as
  // "this focus line is recent." Gate the displayed line on the latter only.
  const focusFresh = isWeekFocusFresh(weekFocus, Date.now());
  const when =
    reviewedAt != null
      ? new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" }).format(
          -Math.round((Date.now() - reviewedAt) / DAY_MS),
          "day"
        )
      : "";

  return (
    <>
      <div className="rounded-2xl border border-amber-200/80 bg-amber-50/60 px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <Sparkles className="w-[18px] h-[18px] text-amber-700 mt-0.5 flex-none" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-950">{t("cockpit_weekly_title")}</p>
            <p className="text-[12.5px] text-amber-800/90">
              {focusFresh && weekFocus?.line
                ? t("cockpit_weekly_focus", { line: weekFocus.line })
                : t("cockpit_weekly_text")}
            </p>
            {fresh && (
              <p className="text-[11px] text-amber-700/70 mt-0.5">{t("cockpit_weekly_reviewed", { when })}</p>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setOpen(true)}
          className="rounded-full border-amber-700/60 text-amber-900 hover:bg-amber-100 min-h-[44px] px-5 flex-none"
        >
          {fresh ? t("cockpit_weekly_again") : t("cockpit_weekly_cta")}
        </Button>
      </div>
      {open && <RitualSession ritualKey="weekly" onClose={() => setOpen(false)} />}
    </>
  );
};
